/**
 * /app/$tenantSlug/posh/training/$moduleId — scenario player for a
 * training module.
 *
 * `?step=` (1-based) is the scene the learner is on, so the phone's Back
 * button steps back through scenes. No step → resume where they left off.
 */

import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useCallback } from "react";
import { ArrowLeft } from "lucide-react";
import { localize } from "@/lib/api";
import { useTrainingModules } from "@/hooks/use-posh";
import { PageHeader } from "@/components/shared/page-header";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { ScenarioPlayer } from "@/components/posh/scenario-player";
import { TrainingLanguageSwitch } from "@/components/posh/training-language-switch";
import { TrainingErrorAlert } from "@/components/posh/training-error-alert";
import { parseStep } from "@/components/posh/training-player-logic";
import { poshText, type PoshLang } from "@/components/posh/posh-ui-text";
import { usePoshLang } from "@/lib/posh-lang";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface PlayerSearch {
  step?: number;
}

export const Route = createFileRoute("/_authgated/app/$tenantSlug/posh/training/$moduleId/")({
  validateSearch: (search: Record<string, unknown>): PlayerSearch => {
    const step = parseStep(search.step);
    return step === undefined ? {} : { step };
  },
  component: ScenarioPlayerRoute,
});

function BackToTraining({ tenantSlug, lang }: { tenantSlug: string; lang: PoshLang }) {
  return (
    <Button asChild variant="outline" size="sm" className="min-h-11 sm:min-h-9">
      <Link to="/app/$tenantSlug/posh/training" params={{ tenantSlug }}>
        {poshText("backToTraining", lang)}
      </Link>
    </Button>
  );
}

function PlayerBody({ moduleId }: { moduleId: number }) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const modulesQ = useTrainingModules();
  const { lang } = usePoshLang();
  const { step } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const onStepChange = useCallback(
    (next: number) => {
      void navigate({ search: { step: next } });
    },
    [navigate],
  );

  if (modulesQ.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-1/2" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (modulesQ.isError) {
    return (
      <TrainingErrorAlert
        title={poshText("loadModuleError", lang)}
        error={modulesQ.error}
        lang={lang}
        onRetry={() => void modulesQ.refetch()}
      />
    );
  }

  const entry = modulesQ.data?.modules.find((m) => m.module.id === moduleId);

  if (!entry) {
    return (
      <Alert lang={lang}>
        <AlertTitle>{poshText("moduleNotFound", lang)}</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>{poshText("moduleNotFoundBody", lang)}</p>
          <BackToTraining tenantSlug={tenantSlug} lang={lang} />
        </AlertDescription>
      </Alert>
    );
  }

  const { module } = entry;

  return (
    <div lang={lang}>
      <div className="mb-4">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 min-h-11 gap-1.5 text-muted-foreground sm:min-h-9"
        >
          <Link to="/app/$tenantSlug/posh/training" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" aria-hidden />
            {poshText("training", lang)}
          </Link>
        </Button>
      </div>
      <PageHeader
        title={localize(module.title, lang) || module.code}
        description={localize(module.description, lang)}
        actions={<TrainingLanguageSwitch />}
      />
      <ScenarioPlayer
        // A different module is a different player (fresh resume point).
        key={module.id}
        module={module}
        progress={entry.progress}
        step={step}
        onStepChange={onStepChange}
      />
    </div>
  );
}

function ScenarioPlayerRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { moduleId } = Route.useParams();
  const { lang } = usePoshLang();
  const id = Number(moduleId);

  return (
    <PoshFeatureGate>
      {Number.isFinite(id) && id > 0 ? (
        <PlayerBody moduleId={id} />
      ) : (
        <Alert variant="destructive" lang={lang}>
          <AlertTitle>{poshText("invalidModule", lang)}</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>{poshText("invalidModuleBody", lang)}</p>
            <BackToTraining tenantSlug={tenantSlug} lang={lang} />
          </AlertDescription>
        </Alert>
      )}
    </PoshFeatureGate>
  );
}
